/**
 * Tageszellen einer Apartmentzeile in der Belegung (Board „Belegung“).
 *
 * Wochenende, Heute und Monatswechsel sind ruhige Spaltensignale. Eine Verbundnacht trägt das
 * Symbol des Wohnungsverbunds auf gestreiftem Grund und bietet keine manuelle Buchung an – der
 * Server lehnte sie ab. Doppelt belegt (zwei eigene Buchungen oder Buchung und Verbund in derselben
 * Nacht) ist rot gestreift unter den Balken. Jeder Grund steht im zugänglichen Namen und in der
 * Legende.
 */
import { Link2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { dayLabel } from './calendarDates';

/**
 * Bewusst ein Typalias, kein Interface: `style` erwartet `CSSProperties`, und Radix' Popper
 * erweitert diesen Typ um eine Pattern-Index-Signatur für seine `--radix-*`-Variablen. Ein
 * Interface bringt keine implizite Index-Signatur mit und ist damit nicht mehr zuweisbar.
 */
export type GridPos = {
  readonly gridColumn: number;
  readonly gridRow: number;
};

export type CalendarCellKind = 'booked' | 'double' | 'family' | 'free';

interface CellProps {
  date: string;
  weekend: boolean;
  isMonthBreak: boolean;
  isToday: boolean;
  gridPos: GridPos;
}

export const FAMILY_CELL_LABEL = 'Durch Verbund belegt';
export const DOUBLE_NIGHT_LABEL = 'Doppelt belegt';

export const familyStripes =
  'bg-[repeating-linear-gradient(135deg,var(--color-card-alt)_0_5px,var(--color-card)_5px_10px)]';
export const doubleStripes =
  'bg-[repeating-linear-gradient(135deg,var(--color-sealwax-tint)_0_5px,var(--color-card)_5px_10px)]';

function cellClasses({ weekend, isMonthBreak, isToday }: CellProps) {
  return cn(
    'border-card-alt relative flex h-full items-center justify-center border-l',
    weekend && 'bg-paper/60',
    isToday && 'bg-forest/6',
    isMonthBreak && 'border-l-ink-faint/40',
  );
}

/** Nur eine wirklich freie Nacht bietet die manuelle Buchung an – auch eine vergangene. */
export function CalendarDayCell({
  kind,
  onBook,
  ...cell
}: CellProps & { kind: CalendarCellKind; onBook?: (anchor: HTMLElement) => void }) {
  const label = dayLabel(cell.date);
  if (kind === 'double') {
    return (
      <div
        style={cell.gridPos}
        aria-label={`${label}: ${DOUBLE_NIGHT_LABEL}`}
        title={DOUBLE_NIGHT_LABEL}
        className={cn(cellClasses(cell), doubleStripes)}
      />
    );
  }
  if (kind === 'family') {
    return (
      <div
        style={cell.gridPos}
        aria-label={`${label}: ${FAMILY_CELL_LABEL}`}
        title={FAMILY_CELL_LABEL}
        className={cn(cellClasses(cell), familyStripes)}
      >
        <Link2 size={13} className="text-ink-faint" aria-hidden />
      </div>
    );
  }
  if (kind === 'free' && onBook) {
    return (
      <button
        type="button"
        data-free-day
        onClick={(event) => onBook(event.currentTarget)}
        style={cell.gridPos}
        className={cn(cellClasses(cell), 'hover:bg-forest/8 cursor-copy transition-colors')}
        title={`${label} – manuelle Buchung anlegen`}
      />
    );
  }
  return <div style={cell.gridPos} className={cellClasses(cell)} />;
}
